"use client";
import { useEffect, useRef } from "react";
import { Phone, PhoneOff, X, Headphones, Loader2 } from "lucide-react";
import { Button } from "../../shadcn/button";
import type { CallStatus } from "@/app/hooks/useLiveSupport";

interface LiveSupportDialogProps {
  callStatus: CallStatus;
  statusMessage: string;
  onClose: () => void;
  onCall: () => void;
  onHangup: () => void;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  onAiCall?: () => void;
}

const LiveSupportDialog = ({
  callStatus,
  statusMessage,
  onClose,
  onCall,
  onHangup,
  triggerRef,
  onAiCall,
}: LiveSupportDialogProps) => {
  const dialogRef = useRef<HTMLDivElement>(null);

  const isActive = callStatus === "active";
  const isCalling = callStatus === "calling";
  const isRegistered = callStatus === "registered";
  const isError = callStatus === "error";
  const isEnded = callStatus === "ended";
  const isWaiting =
    !isRegistered && !isCalling && !isActive && !isError && !isEnded;

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;

      // dialog içindeyse çık
      if (dialogRef.current?.contains(target)) return;

      // buton ise çık
      if (triggerRef.current?.contains(target)) return;

      onClose();
    };

    document.addEventListener("pointerdown", handleClickOutside, true); // 👈 KRİTİK

    return () => {
      document.removeEventListener("pointerdown", handleClickOutside, true);
    };
  }, [onClose, triggerRef]);

  return (
    <div
      ref={dialogRef}
      className="fixed bottom-6 right-18 z-50 w-76 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden animate-in slide-in-from-bottom-3 fade-in duration-200"
    >
      {/* Header */}
      <div className="relative px-5 pt-5 pb-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-11 h-11 bg-gray-900 rounded-2xl flex items-center justify-center">
                <Headphones size={20} className="text-white" />
              </div>
              <span
                className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white transition-colors duration-500 ${
                  isActive
                    ? "bg-green-400"
                    : isCalling
                      ? "bg-blue-400 animate-pulse"
                      : isRegistered
                        ? "bg-green-400"
                        : isError
                          ? "bg-red-400"
                          : "bg-gray-300"
                }`}
              />
            </div>
            <div>
              <p className="font-semibold text-sm text-gray-900">
                Canlı Destek
              </p>
              <p className="text-xs text-gray-400 mt-0.5">Müşteri Hizmetleri</p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Divider */}
      <div className="h-px bg-gray-100 mx-5" />

      {/* Body */}
      <div className="px-5 py-4 space-y-4">
        {/* State area */}
        <div className="min-h-16 flex flex-col items-center justify-center">
          {isWaiting && (
            <div className="flex flex-col items-center gap-2">
              <Loader2 size={22} className="text-gray-400 animate-spin" />
              <p className="text-xs text-gray-400">{statusMessage}</p>
            </div>
          )}

          {isRegistered && (
            <div className="flex flex-col items-center gap-1.5 text-center">
              <div className="flex gap-1">
                {[...Array(3)].map((_, i) => (
                  <span
                    key={i}
                    className="w-1.5 h-1.5 rounded-full bg-green-400"
                  />
                ))}
              </div>
              <p className="text-xs font-medium text-green-600">
                Temsilciye bağlanmaya hazır
              </p>
            </div>
          )}

          {isCalling && (
            <div className="flex flex-col items-center gap-2 text-center">
              <div className="flex gap-1 items-end">
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className="w-1 bg-blue-400 rounded-full animate-pulse"
                    style={{
                      height: `${10 + i * 4}px`,
                      animationDelay: `${i * 0.12}s`,
                    }}
                  />
                ))}
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i + 4}
                    className="w-1 bg-blue-400 rounded-full animate-pulse"
                    style={{
                      height: `${22 - i * 4}px`,
                      animationDelay: `${(i + 4) * 0.12}s`,
                    }}
                  />
                ))}
              </div>
              <p className="text-xs font-medium text-blue-600">
                Temsilci aranıyor...
              </p>
            </div>
          )}

          {isActive && (
            <div className="flex flex-col items-center gap-2 text-center">
              <div className="flex gap-0.5 items-center">
                {[...Array(9)].map((_, i) => (
                  <div
                    key={i}
                    className="w-1 bg-green-500 rounded-full animate-pulse"
                    style={{
                      height: `${8 + Math.abs(4 - i) * 3}px`,
                      animationDelay: `${i * 0.1}s`,
                      animationDuration: `${0.6 + (i % 3) * 0.2}s`,
                    }}
                  />
                ))}
              </div>
              <p className="text-xs font-medium text-green-600">
                Görüşme devam ediyor
              </p>
            </div>
          )}

          {isEnded && (
            <div className="flex flex-col items-center gap-1.5 text-center">
              <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                <PhoneOff size={15} className="text-gray-400" />
              </div>
              <p className="text-xs text-gray-500">Görüşme sona erdi</p>
            </div>
          )}

          {isError && (
            <div className="flex flex-col items-center gap-1.5 text-center">
              <div className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center">
                <span className="text-base">⚠️</span>
              </div>
              <p className="text-xs text-red-500">{statusMessage}</p>
            </div>
          )}
        </div>

        {(isRegistered || isEnded) && (
          onAiCall ? (
            <div className="flex gap-2">
              {/* <Button
                onClick={onAiCall}
                className="flex-1 h-11 rounded-xl gap-2 text-sm font-medium transition-all"
              >
                <Phone size={15} />
                AI Temsilci Ara
              </Button> */}
              <Button
                onClick={onCall}
                className="w-full h-11 rounded-xl gap-2 text-sm font-medium transition-all"
              >
                <Phone size={15} />
                Temsilci Ara
              </Button>
            </div>
          ) : (
            <Button
              onClick={onCall}
              className="w-full h-11 rounded-xl gap-2 text-sm font-medium transition-all"
            >
              <Phone size={15} />
              {isEnded ? "Tekrar Ara" : "Temsilci Ara"}
            </Button>
          )
        )}

        {(isCalling || isActive) && (
          <Button
            onClick={onHangup}
            className="w-full h-11 bg-red-500 hover:bg-red-600 text-white rounded-xl gap-2 text-sm font-medium transition-all"
          >
            <PhoneOff size={15} />
            {isCalling ? "Aramayı İptal Et" : "Görüşmeyi Bitir"}
          </Button>
        )}

        {isError && (
          <Button
            onClick={onCall}
            variant="outline"
            className="w-full h-11 rounded-xl gap-2 text-sm font-medium"
          >
            <Phone size={15} />
            Tekrar Dene
          </Button>
        )}
      </div>

      <div className="px-5 pb-4">
        <p className="text-center text-xs text-gray-800">
          Bağlantı için mikrofon izni gereklidir
        </p>
      </div>
    </div>
  );
};

export default LiveSupportDialog;
